// Serialized products always require globally unique serials within the product.
// Whether a product needs serial scanning is derived from
// `required_serial_numbers_count > 0` (no separate flag).

export interface SerialValidationFlags {
  serial_number_pattern?: string | null;
  box_serial_number_delimiter?: string | null;
  product_barcode?: string | null;
}

export function parseSerials(raw: string, delimiter?: string | null): string[] {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return [];
  if (!delimiter || delimiter.length === 0) return [trimmed];
  return trimmed
    .split(delimiter)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

// `invalid: true` 代表 caller 給了非空 pattern 但解析失敗（壞 regex）— 上層必須當作
// invariant violation，鎖死 submit 並上報，**不可** silently fall through 成「無 pattern」。
// `invalid: false, regex: null` 才是「沒設定 pattern，預設不檢查」的合法狀態。
export interface CompiledPattern {
  regex: RegExp | null;
  invalid: boolean;
}

export function compilePattern(pattern?: string | null): CompiledPattern {
  if (!pattern || pattern.length === 0) return { regex: null, invalid: false };
  try {
    return { regex: new RegExp(pattern), invalid: false };
  } catch {
    return { regex: null, invalid: true };
  }
}

export type ValidationFailure =
  | { ok: false; reason: 'pattern_mismatch'; serial: string; pattern: string }
  | { ok: false; reason: 'duplicate'; serial: string }
  | { ok: false; reason: 'max_reached' }
  | { ok: false; reason: 'is_product_barcode'; serial: string };

export type ValidationResult = { ok: true } | ValidationFailure;

export interface ValidateContext {
  pattern: RegExp | null;
  existing: Set<string>;
  remainingSlots: number;
  productBarcode?: string | null;
}

export function validateSerial(serial: string, ctx: ValidateContext): ValidationResult {
  if (ctx.remainingSlots <= 0) {
    return { ok: false, reason: 'max_reached' };
  }
  if (ctx.productBarcode && serial === ctx.productBarcode) {
    return { ok: false, reason: 'is_product_barcode', serial };
  }
  if (ctx.pattern && !ctx.pattern.test(serial)) {
    return { ok: false, reason: 'pattern_mismatch', serial, pattern: ctx.pattern.toString() };
  }
  if (ctx.existing.has(serial)) {
    return { ok: false, reason: 'duplicate', serial };
  }
  return { ok: true };
}

export function validateBatch(
  serials: string[],
  ctx: ValidateContext,
): { accepted: string[]; failure: ValidationFailure | null } {
  const accepted: string[] = [];
  const runningExisting = new Set(ctx.existing);
  let runningSlots = ctx.remainingSlots;

  for (const sn of serials) {
    const result = validateSerial(sn, {
      ...ctx,
      existing: runningExisting,
      remainingSlots: runningSlots,
    });
    if (!result.ok) {
      return { accepted, failure: result };
    }
    accepted.push(sn);
    runningExisting.add(sn);
    runningSlots -= 1;
  }
  return { accepted, failure: null };
}
